import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Handshake } from "@mui/icons-material";
import CustomDataGrid from '../../../../../components/ui/customDataGrid/customDataGrid.component';
import { CONTRACT_TERMS_ENDPOINT } from '../../../../../consts';
import { useHasPermission } from '../../../../../contexts/auth';
import { ContractTermList } from '../../../../../types/contracts';
import { ContractEndDateDisplay, formatContractEndDate, formatNumber } from '../../../../../utils';
import EntityReferenceLink from '../../../../../components/ui/entityReference/entityReferenceLink.component';
import { CONTRACT_TERM_KIND_OPTIONS, SETTLEMENT_CYCLE_OPTIONS } from '../../../../../consts';
import {
  containsStringFilterOperators,
  equalsNumberFilterOperators,
  exactDateFilterOperators,
  singleSelectFilterOperators,
} from '../../../../../components/ui/customDataGrid/filterOperators';

export const EquipmentContracts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewContracts = useHasPermission("settlement.view_contractterm");
  const canViewContract = useHasPermission("settlement.view_contract");

  const KIND_OPTIONS = useMemo(
    () => CONTRACT_TERM_KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );
  const SETTLEMENT_CYCLE_SELECT_OPTIONS = useMemo(
    () => SETTLEMENT_CYCLE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "contract__number",
      headerName: t("Contract Number"),
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value, row) => row.contract?.number || "-",
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="contract"
          entityId={params.row.contract?.id}
          label={params.row.contract?.number || "-"}
          canPreview={canViewContract}
        />
      ),
    },
    {
      field: "contract__internal_name",
      headerName: t("Internal name"),
      filterOperators: containsStringFilterOperators(),
      valueGetter: (_value, row) => row.contract?.internal_name || "-",
    },
    {
      field: "contract__start_date",
      headerName: t("Contract Start Date"),
      filterOperators: exactDateFilterOperators(),
      valueGetter: (value, row) => row.contract?.start_date || "-",
    },
    {
      field: "contract__end_date",
      headerName: t("Contract End Date"),
      filterable: false,
      valueGetter: (value, row) =>
        formatContractEndDate(
          row.contract?.end_date,
          row.contract?.indefinite_after_end_date,
          t
        ),
      renderCell: (params) => (
        <ContractEndDateDisplay
          endDate={params.row.contract?.end_date}
          indefiniteAfterEndDate={params.row.contract?.indefinite_after_end_date}
          t={t}
        />
      ),
    },
    {
      field: "kind",
      headerName: t("Kind"),
      type: "singleSelect",
      valueOptions: KIND_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.kind,
      renderCell: (params) => params.row.kind_display,
    },
    {
      field: "settlement_cycle",
      headerName: t("Settlement Cycle"),
      type: "singleSelect",
      valueOptions: SETTLEMENT_CYCLE_SELECT_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.settlement_cycle,
      renderCell: (params) => params.row.settlement_cycle_display,
    },
    {
      field: "rent_price",
      headerName: t("Rent Price"),
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      valueFormatter: (value) => formatNumber(value),
    },
  ], [t, canViewContract, KIND_OPTIONS, SETTLEMENT_CYCLE_SELECT_OPTIONS]);

  const handleRowClick = (id: string, row?: ContractTermList) => {
    navigate(`/contracts/${row?.contract?.id}`);
  };

  if (!canViewContracts) {
    return null;
  }

  const contractsUrl = id ? `${CONTRACT_TERMS_ENDPOINT}?equipment=${id}` : CONTRACT_TERMS_ENDPOINT;

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<Handshake />}
        title={t('Assigned Contracts')}
        url={contractsUrl}
        gridSettingsKey="EQUIPMENT_CONTRACTS"
        columns={columns}
        onRowClick={handleRowClick}
        refreshEventName="equipmentContractAdded"
      />
    </Box>
  );
};
